import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import { useAuth } from "./AuthContext.jsx";

const STORAGE_PREFIX = "catalog_saved_state";

const initialState = {
  items: [],
};

function sanitizeSavedItem(rawItem) {
  if (!rawItem || rawItem.id === undefined || rawItem.id === null) {
    return null;
  }
  const imageCandidates = [];
  if (Array.isArray(rawItem.images)) {
    for (const value of rawItem.images) {
      if (value) {
        imageCandidates.push(value);
      }
    }
  }
  if (rawItem.image_url) {
    imageCandidates.unshift(rawItem.image_url);
  }
  if (rawItem.image) {
    imageCandidates.unshift(rawItem.image);
  }
  const imageUrl = imageCandidates
    .map((value) => {
      try {
        return String(value).trim();
      } catch (_err) {
        return "";
      }
    })
    .find((value) => Boolean(value)) || null;

  const itemSnapshot = {
    ...rawItem,
    image_url: imageUrl,
  };

  return {
    id: rawItem.id,
    item: itemSnapshot,
    savedAt: Date.now(),
  };
}

function normalizeStoredItems(entries) {
  if (!Array.isArray(entries)) {
    return [];
  }
  return entries
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }
      if (entry.id === undefined || entry.id === null) {
        return null;
      }
      const rawItem = entry.item ?? {};
      const sanitized = sanitizeSavedItem({ ...rawItem, id: entry.id });
      if (!sanitized) {
        return null;
      }
      return {
        ...sanitized,
        savedAt: typeof entry.savedAt === "number" ? entry.savedAt : sanitized.savedAt,
      };
    })
    .filter(Boolean);
}

function savedReducer(state, action) {
  switch (action.type) {
    case "RESTORE_STATE": {
      const items = normalizeStoredItems(action.payload?.items);
      return {
        ...state,
        items,
      };
    }
    case "SAVE_ITEM": {
      const entry = sanitizeSavedItem(action.payload);
      if (!entry) {
        return state;
      }
      if (state.items.some((item) => item.id === entry.id)) {
        return state;
      }
      return {
        ...state,
        items: [entry, ...state.items],
      };
    }
    case "REMOVE_ITEM": {
      const targetId = action.payload;
      if (targetId === undefined || targetId === null) {
        return state;
      }
      return {
        ...state,
        items: state.items.filter((entry) => entry.id !== targetId),
      };
    }
    case "CLEAR_ALL": {
      if (!state.items.length) {
        return state;
      }
      return {
        ...state,
        items: [],
      };
    }
    default:
      return state;
  }
}

function getStorageKey(userId) {
  return `${STORAGE_PREFIX}:${userId || "guest"}`;
}

function readStoredState(key) {
  if (typeof window === "undefined" || !window.localStorage) {
    return initialState;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return initialState;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return initialState;
    }
    const items = normalizeStoredItems(parsed.items);
    return { items };
  } catch (error) {
    console.warn("Failed to parse saved items from storage", error);
    return initialState;
  }
}

const SavedContext = createContext({
  items: [],
  isSaved: () => false,
  saveItem: () => false,
  removeItem: () => {},
  toggleSaved: () => false,
  clearAll: () => {},
  isOpen: false,
  openPanel: () => {},
  closePanel: () => {},
  togglePanel: () => {},
});

export function SavedProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const storageKey = useMemo(() => getStorageKey(userId), [userId]);

  const [state, dispatch] = useReducer(
    savedReducer,
    initialState,
    () => readStoredState(storageKey),
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const snapshot = readStoredState(storageKey);
    dispatch({ type: "RESTORE_STATE", payload: snapshot });
    setIsOpen(false);
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          items: state.items.map((entry) => ({
            id: entry.id,
            item: entry.item,
            savedAt: entry.savedAt,
          })),
        }),
      );
    } catch (error) {
      console.warn("Failed to persist saved items", error);
    }
  }, [state.items, storageKey]);

  const isSaved = useCallback(
    (itemId) => state.items.some((entry) => entry.id === itemId),
    [state.items],
  );

  const saveItem = useCallback(
    (item) => {
      if (!item || item.id === undefined || item.id === null) {
        return false;
      }
      if (isSaved(item.id)) {
        return false;
      }
      dispatch({ type: "SAVE_ITEM", payload: item });
      setIsOpen(true);
      return true;
    },
    [isSaved],
  );

  const removeItem = useCallback((itemId) => {
    dispatch({ type: "REMOVE_ITEM", payload: itemId });
  }, []);

  const toggleSaved = useCallback(
    (item) => {
      if (!item || item.id === undefined || item.id === null) {
        return false;
      }
      if (isSaved(item.id)) {
        dispatch({ type: "REMOVE_ITEM", payload: item.id });
        return false;
      }
      dispatch({ type: "SAVE_ITEM", payload: item });
      setIsOpen(true);
      return true;
    },
    [isSaved],
  );

  const clearAll = useCallback(() => {
    dispatch({ type: "CLEAR_ALL" });
  }, []);

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => {
    setIsOpen((value) => !value);
  }, []);

  const contextValue = useMemo(
    () => ({
      items: state.items,
      isSaved,
      saveItem,
      removeItem,
      toggleSaved,
      clearAll,
      isOpen,
      openPanel,
      closePanel,
      togglePanel,
    }),
    [
      state.items,
      isSaved,
      saveItem,
      removeItem,
      toggleSaved,
      clearAll,
      isOpen,
      openPanel,
      closePanel,
      togglePanel,
    ],
  );

  return (
    <SavedContext.Provider value={contextValue}>
      {children}
    </SavedContext.Provider>
  );
}

export function useSaved() {
  return useContext(SavedContext);
}
